import { ChevronLeft, ChevronRight } from "lucide-react";
import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type Ref } from "react";
import { DATUM_TEXTE, imRahmen, monatPlus, monatsAnfang, monatsName, monatsRaster, tagLesetext, tagPlus, wochenkopf, wochentag } from "./datum";

/** W82 Web-5h-N2b (b1, Frage 61 = B): Monatsblatt unter dem Datumsfeld, im Blatt und im Fluss (nie schwebend, kein neues Blatt).
 *  Kopf DW03 mit DW04 und DW05, Wochenkopf ab Montag (DW06), Tage als Knöpfe 44 x 44; heute `aria-current="date"`, der gewählte
 *  Tag in der Knopffarbe (`aria-pressed`), Tage außerhalb von `min` und `max` gesperrt (`aria-disabled`, bleiben per Pfeil
 *  erreichbar). Tastatur nach dem APG-Muster „Date Picker Dialog“: Pfeile je Tag und Woche, Bild auf und ab je Monat, Pos1 und
 *  Ende je Woche, Enter und Leertaste wählen; Escape fängt das Datumsfeld (Feld.tsx). Keine eigene Bewegung, Monatswechsel ohne
 *  Rollen. `data-feld-halt`: Knöpfe halten die Lage des Blatts wie ein Feld (Sheet.tsx, HW21); ein Druck nimmt dem
 *  fokussierten Knopf den Fokus nicht (Safari gäbe ihn sonst an body ab, die Fokuswache legte ihn auf „Abbrechen“). */

const KOPF = wochenkopf();

/** Druck ohne Fokuswechsel: der Fokus bleibt, wo er ist, bis der Klick ihn selbst setzt. */
const haltenBeimDruck = (e: MouseEvent) => e.preventDefault();

type Props = {
  id: string;
  /** gewählter Tag (JJJJ-MM-TT) oder leer */
  wert: string;
  heute: string;
  min?: string;
  max?: string;
  onWahl: (iso: string) => void;
  ref?: Ref<HTMLDivElement>;
};

export function Monatsblatt({ id, wert, heute, min, max, onWahl, ref }: Props) {
  const kopfId = useId();
  // Tag mit dem Fokus (roving tabindex); beim Öffnen der gewählte Tag, sonst heute, in den Grenzen
  const [fokus, setFokus] = useState(() => {
    const start = wert || heute;
    return min && start < min ? min : max && start > max ? max : start;
  });
  const raster = useRef<HTMLDivElement>(null);
  const tastatur = useRef(false);
  const anfang = monatsAnfang(fokus);
  const naechster = monatPlus(anfang, 1);
  const vorigerGeht = !min || tagPlus(anfang, -1) >= min;
  const naechsterGeht = !max || naechster <= max;

  // nach einem Tastenschritt bekommt der neue Tag den Fokus (auch im neuen Monat)
  useLayoutEffect(() => {
    if (!tastatur.current) return;
    tastatur.current = false;
    raster.current?.querySelector<HTMLButtonElement>(`[data-tag="${fokus}"]`)?.focus();
  }, [fokus]);

  // per Tastatur nie über den ersten oder letzten erlaubten Tag hinaus (die gesperrten Tage davor stehen im Raster)
  const gehe = (ziel: string) => {
    tastatur.current = true;
    setFokus(min && ziel < min ? min : max && ziel > max ? max : ziel);
  };

  const taste = (e: KeyboardEvent<HTMLButtonElement>, tag: string) => {
    const schritte: Record<string, () => string> = {
      ArrowLeft: () => tagPlus(tag, -1),
      ArrowRight: () => tagPlus(tag, 1),
      ArrowUp: () => tagPlus(tag, -7),
      ArrowDown: () => tagPlus(tag, 7),
      PageUp: () => monatPlus(tag, e.shiftKey ? -12 : -1),
      PageDown: () => monatPlus(tag, e.shiftKey ? 12 : 1),
      Home: () => tagPlus(tag, -wochentag(tag)),
      End: () => tagPlus(tag, 6 - wochentag(tag)),
    };
    const schritt = schritte[e.key];
    if (schritt) {
      e.preventDefault();
      gehe(schritt());
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!e.repeat && imRahmen(tag, min, max)) onWahl(tag);
    }
  };

  // Monatswechsel per Pfeil: gleicher Tag im anderen Monat, in den Grenzen; der Fokus bleibt auf dem Pfeil (am Rand gesperrt per
  // aria-disabled, nicht disabled: ein gesperrter Knopf verlöre den Fokus an body). R1: der Druck nimmt dem Tag den Fokus nicht, also
  // legt der Klick ihn selbst auf den Pfeil; sonst fiele er mit dem Tag des alten Monats auf body (Escape schlösse dann das Blatt)
  const wechseln = (e: MouseEvent<HTMLButtonElement>, geht: boolean, ziel: string) => {
    e.currentTarget.focus({ preventScroll: true });
    if (geht) setFokus(min && ziel < min ? min : max && ziel > max ? max : ziel);
  };

  return (
    <div ref={ref} id={id} className="monatsblatt" data-esc-eigen="" data-feld-halt-bereich="">
      <div className="monatsblatt-kopf flex items-center gap-2">
        {/* DW03: Name des Rasters; beim Monatswechsel einmal angesagt, nicht je Taste im selben Monat */}
        <h3 id={kopfId} aria-live="polite" className="monatsblatt-titel t-headline min-w-0 flex-1 px-2">
          {monatsName(anfang)}
        </h3>
        <button
          type="button"
          aria-label={DATUM_TEXTE.voriger}
          aria-disabled={vorigerGeht ? undefined : true}
          data-feld-halt=""
          onMouseDown={haltenBeimDruck}
          onClick={(e) => wechseln(e, vorigerGeht, monatPlus(fokus, -1))}
          className="monatsblatt-pfeil grid size-(--touch) shrink-0 place-items-center rounded-full text-pg-accent-text aria-disabled:text-pg-text-3"
        >
          <ChevronLeft size="1.2em" strokeWidth={2} aria-hidden />
        </button>
        <button
          type="button"
          aria-label={DATUM_TEXTE.naechster}
          aria-disabled={naechsterGeht ? undefined : true}
          data-feld-halt=""
          onMouseDown={haltenBeimDruck}
          onClick={(e) => wechseln(e, naechsterGeht, monatPlus(fokus, 1))}
          className="monatsblatt-pfeil grid size-(--touch) shrink-0 place-items-center rounded-full text-pg-accent-text aria-disabled:text-pg-text-3"
        >
          <ChevronRight size="1.2em" strokeWidth={2} aria-hidden />
        </button>
      </div>
      <div ref={raster} role="grid" aria-labelledby={kopfId} className="monatsblatt-raster" data-monat={anfang.slice(0, 7)}>
        <div role="row" className="monatsblatt-zeile">
          {KOPF.map((w) => (
            <span key={w.lang} role="columnheader" aria-label={w.lang} className="monatsblatt-wochentag t-caption-1 t-caption-1-medium text-pg-text-2">
              <span aria-hidden>{w.kurz}</span>
            </span>
          ))}
        </div>
        {monatsRaster(anfang).map((woche, w) => (
          <div key={w} role="row" className="monatsblatt-zeile">
            {woche.map((tag, d) =>
              tag ? (
                <span key={tag} role="gridcell" className="monatsblatt-zelle">
                  <button
                    type="button"
                    data-tag={tag}
                    data-feld-halt=""
                    tabIndex={tag === fokus ? 0 : -1}
                    aria-label={tagLesetext(tag, heute)}
                    aria-pressed={tag === wert}
                    aria-current={tag === heute ? "date" : undefined}
                    aria-disabled={imRahmen(tag, min, max) ? undefined : true}
                    onMouseDown={haltenBeimDruck}
                    onFocus={() => setFokus(tag)}
                    onKeyDown={(e) => taste(e, tag)}
                    // Enter und Leertaste wählen schon im keydown (preventDefault: kein zweiter Klick beim Loslassen)
                    onClick={() => imRahmen(tag, min, max) && onWahl(tag)}
                    className="monatsblatt-tag t-body t-zahl"
                  >
                    {Number(tag.slice(8))}
                  </button>
                </span>
              ) : (
                <span key={`l${w}-${d}`} role="gridcell" className="monatsblatt-zelle" />
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
